import React, { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import Modal from '../../components/Modal';
import PlayerPicker from '../players/PlayerPicker';
import ShareLeague from './ShareLeague';
import * as miniLeaguesApi from '../../api/miniLeagues';
import * as playersApi from '../../api/players';

/**
 * Start a new league with the same tournaments and the same crowd.
 *
 * Any member can do this, not just the manager — it is a growth feature, and
 * needing permission to start your own league would defeat it.
 *
 * The picker's selection used to be dropped on the floor: a fork carried
 * tournaments and you were left to share a code with whoever you had ticked.
 * It now travels with the request, and everyone picked gets an invitation to
 * accept or decline. The code is still there for anyone you could not name.
 */
function ForkLeagueModal({ league, currentUser, onClose, onForked }) {
  const [name, setName] = useState(`${league.name} II`);
  const [tournamentIds, setTournamentIds] = useState(
    (league.tournaments || []).filter((t) => t.tournamentActive).map((t) => t.tournamentId)
  );
  const [playedWith, setPlayedWith] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    playersApi.getPlayedWith()
      .then((list) => {
        const others = (list || []).filter((p) => String(p.playerId) !== String(currentUser?.id));
        setPlayedWith(others);
        // Everyone in by default — the usual case is the whole crowd regrouping.
        setSelectedIds(others.map((p) => p.playerId));
      })
      .catch(() => setPlayedWith([]));
  }, [currentUser]);

  const toggleTournament = (id) =>
    setTournamentIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  const togglePlayer = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const forked = await miniLeaguesApi.forkLeague(league.id, {
        name,
        tournamentIds,
        inviteePlayerIds: selectedIds,
      });
      setCreated(forked);
      if (onForked) onForked(forked);
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'Could not create the league' : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(created.leagueCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (created) {
    const shortlist = playedWith.filter((p) => selectedIds.includes(p.playerId));
    const shortlistCount = shortlist.length;
    return (
      <Modal title={`${created.name} is ready`} onClose={onClose}>
        <div className="fork-done">
          <p>
            {shortlistCount === 0
              ? 'Share this code and they are straight in — nothing has started yet.'
              : 'Invitations are on their way. Share the code with anyone else you want in.'}
          </p>
          <div className="game-code-display">
            <span className="code-value">{created.leagueCode}</span>
            <button className="copy-btn" onClick={copyCode} title="Copy league code">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <ShareLeague league={{ ...created, joinOpen: true }} />
          </div>
          {shortlist.length > 0 && (
            <>
              <p className="helper-text">Invited:</p>
              <p className="fork-shortlist">{shortlist.map((p) => p.displayName).join(', ')}</p>
            </>
          )}
          <button type="button" className="primary-btn full-width" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  const tournaments = league.tournaments || [];

  return (
    <Modal title="Start a new league with these players" onClose={onClose}>
      <div className="form-group">
        <label htmlFor="fork-name">League name</label>
        <input
          id="fork-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
        />
      </div>

      <div className="form-group">
        <label>Tournaments to carry over</label>
        {tournaments.map((t) => (
          <label key={t.id} className="checkbox-row">
            <input
              type="checkbox"
              checked={tournamentIds.includes(t.tournamentId)}
              onChange={() => toggleTournament(t.tournamentId)}
              aria-label={t.tournamentName}
            />
            <span>{t.tournamentName}</span>
          </label>
        ))}
        <p className="helper-text">
          Each one starts from the week the tournament has actually reached.
        </p>
      </div>

      <div className="form-group">
        <label>Who to invite</label>
        <PlayerPicker
          players={playedWith}
          selectedIds={selectedIds}
          onToggle={togglePlayer}
          emptyHint="Once you've played some games, the people you play with most show up here."
        />
        <p className="helper-text">
          They each get an invitation to accept or decline — nobody is added without
          agreeing. You'll get a code too, for anyone not on this list.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <button
        type="button"
        className="primary-btn full-width"
        onClick={submit}
        disabled={busy || tournamentIds.length === 0}
      >
        {busy ? 'Creating...' : `Create league for ${selectedIds.length + 1} players`}
      </button>
    </Modal>
  );
}

export default ForkLeagueModal;
